// Row-level array positional ops — `first`, `last`, `nth`. Registered in
// `opMeta` (the top-level op registry), so they are usable in any
// expression context: derive bodies, filter predicates, top-level
// bindings.
//
// Scope note: `first` and `last` ALSO exist as AGGREGATORS in
// `agg-funcs.ts`. Those register into the SEPARATE `aggMeta` registry and
// are reachable only as child scopes of `aggregate`/`time-bucket`. The two
// registries build independent name tables, so a row-level `first` here and
// an agg-context `first` there share no namespace and never mis-resolve.
//
// Null convention: a null/undefined array ⇒ null; an empty array or an
// out-of-range index ⇒ null (friendly positional access).

import { z } from 'zod';

import { defineFold } from '../../builtins/helpers.ts';
import { opMeta } from '../../op-meta.ts';
import { paramPath } from '../../path.ts';
import { arrayElementType, type ColumnType, nullableCol } from '../../schema/types.ts';
import type { Value } from '../../value.ts';

/**
 * SSOT for the three positional accessors. Each shares: a null/undefined
 * array ⇒ null guard, and a result type of the array arg's ELEMENT type
 * (one `array` layer unwrapped) widened nullable (the access can miss).
 * Ops differ only in the reducer over the non-null element list. The
 * computed type is overridden at the `expr.inferType` slot — same pattern
 * as `coalesce` — since `defineFold`'s fixed `resultType` cannot depend on
 * the argument type.
 */
const positionalArrayOp = (reducer: (els: readonly Value[], rest: readonly unknown[]) => Value) => {
  const fold = defineFold<unknown>((vs) => {
    const [arr, ...rest] = vs;
    return arr === null || arr === undefined ? null : reducer(arr as readonly Value[], rest);
  });
  return {
    ...fold,
    expr: {
      ...fold.expr,
      inferType: (
        call: { readonly params?: readonly unknown[] },
        infer: (node: unknown, path: string) => ColumnType,
        path: string,
      ): ColumnType => nullableCol(arrayElementType(infer((call.params ?? [])[0], paramPath(path, 0)))),
    },
  };
};

export const first = z
  .object({ name: z.literal('first'), params: z.tuple([z.unknown()]) })
  .describe('(first arr) — first element, or null if the array is empty/null.')
  .register(opMeta, {
    ...positionalArrayOp((els) => (els.length === 0 ? null : (els[0] ?? null))),
    example: { expr: '(first ["a" "b" "c"])', result: '"a"' },
  });

export const last = z
  .object({ name: z.literal('last'), params: z.tuple([z.unknown()]) })
  .describe('(last arr) — last element, or null if the array is empty/null.')
  .register(opMeta, {
    ...positionalArrayOp((els) => (els.length === 0 ? null : (els.at(-1) ?? null))),
    example: { expr: '(last ["a" "b" "c"])', result: '"c"' },
  });

export const nth = z
  .object({ name: z.literal('nth'), params: z.tuple([z.unknown(), z.unknown()]) })
  .describe('(nth arr i) — 0-based element at index i, or null if out of range/null.')
  .register(opMeta, {
    ...positionalArrayOp((els, rest) => {
      const idx = Number(rest[0]);
      return idx < 0 || idx >= els.length ? null : (els[idx] ?? null);
    }),
    example: { expr: '(nth ["a" "b" "c"] 1)', result: '"b"' },
  });

/** All array positional ops — composed into `transformOps`. */
export const arrayFuncs = [first, last, nth] as const;
